import type { Logger } from '@devhacker/shared/libs/logger';
import { createLoggerInterceptors } from '@devhacker/shared/libs/logger';
import type { AxiosLikeFetchResponse } from '@devhacker/shared/libs/fetch/createAxiosLikeFetch';
import { createAxiosLikeFetch } from '@devhacker/shared/libs/fetch/createAxiosLikeFetch';
import { parseJsonBigIntIds } from '@devhacker/shared/libs/fetch/parseJsonBigIntIds';
import type { ReactionResponse } from '~/types/api/reactions';
import type { AvailableReaction } from '~/types/components/Reactions';

import { API_REACTIONS_URL } from '~/constants/config';
import { API_TIMEOUT } from '~/constants';
import { useXUserId } from '@devhacker/shared/composables/useXUserId';
import { requestInterceptors } from '~/utils/interceptorsRequestAuth';

const API_PREFIX = '/api/v1/posts';

export interface IReactionsApi {
  fetchReactions: (slug: string) => Promise<
    AxiosLikeFetchResponse<{
      data: ReactionResponse;
      success: boolean;
      meta: {
        last_modified: number | null;
      };
    }>
  >;

  updateReaction: (
    slug: string,
    user_state: AvailableReaction,
  ) => Promise<
    AxiosLikeFetchResponse<{
      success: boolean;
      data: ReactionResponse;
    }>
  >;
}

type Params = {
  $logger: Logger;
};

export default ({ $logger }: Params): IReactionsApi => {
  const loggerInterceptors = createLoggerInterceptors($logger, 'reactions');

  const reactionsFetch = createAxiosLikeFetch({
    baseURL: API_REACTIONS_URL,
    timeout: API_TIMEOUT,
    ...loggerInterceptors,
    parseResponse: parseJsonBigIntIds, // без этого API может вернуть 200 код и html
    onRequest: [
      loggerInterceptors.onRequest,
      requestInterceptors,
      ({ options }) => {
        const xUserId = useXUserId();
        options.headers.set('X-User-Id', String(xUserId));
      },
    ],
  });

  return {
    fetchReactions(slug: string) {
      return reactionsFetch(`${API_PREFIX}/by-post-slug/${slug}`);
    },
    updateReaction(post_slug: string, user_state: AvailableReaction | null) {
      const body = {
        post_type: 'post',
        post_slug,
        user_state,
      };
      return reactionsFetch(API_PREFIX, { method: 'POST', body });
    },
  };
};
